import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import queryString from 'query-string'
import { useSearchParams } from 'react-router-dom'
import { TunerEditorProvider } from 'builder/components/Tuner'
import {
  SectionNames,
  actions,
  selectors,
  ResumeEditorStore,
  ResumeSectionId,
  AddCustomSectionPayload,
  MoveSectionPayload,
  AutoTailoringFromPublicJobBoardStep,
  DOWNLOAD_CURRENT_DOCUMENT_FORMAT_URL_PARAM,
  DownloadDocumentHandler,
} from 'builder/modules/resumeEditor'
import PersonalDetailsSection from 'builder/components/PersonalDetailsSection'
import SectionsList from 'builder/components/SectionsList'
import EditorTitle from 'builder/components/EditorTitle'
import LanguageSelect from 'builder/components/LanguageSelect'
import ResumeScore from 'builder/components/ResumeScore'
import Anchor from 'builder/components/Anchor'
import {
  FetchStatuses,
  FORMATS,
  GenerateResumeStatus,
  ResumeUploadStatus,
  ResumeValidationStatus,
} from 'builder/modules/constants'
import CreateResumeWithAI from 'builder/components/FillResumeModal/CreateResumeWithAI'
import { useConfig } from 'builder/hooks/useConfig'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { EditorPanelType, GeneralEditorStore } from 'builder/modules/generalEditor'
import {
  CardOperationFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from 'builder/components/Card'
import {
  EditorPanelSwitcher,
  EditorPanelSwitcherItem,
} from 'builder/components/EditorPanelSwitcher'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { actions as uiActions, selectors as uiSelectors } from 'builder/modules/ui'
import AiCoach from 'builder/features/conversationalBuilder/assets/AiCoach'
import ConversationalBuilder from 'builder/features/conversationalBuilder/ConversationalBuilder'
import { useScrollState } from 'builder/hooks/useScrollState'
import loaderImage from 'images/builder/resume-editor/editor-loader.svg'
import FillResumeModal, { FillResumeByUrl } from '../FillResumeModal'
import { ATS_TEMPLATES, TemplateName } from '../Helper/constants'
import UseExample from '../FillResumeModal/UseExample'
import JobSearchButton from '../FloatingCIOHelperButton/JobSearchButton/JobSearchButton'
import TailorResumeBanner from '../ImproveResumePanel/components/TailorResumeBanner/TailorResumeBanner'
import { BeforeAutoTailoringKeywordsModal } from '../AutoTailoring/BeforeAutoTailoring'
import { AutoTailoringModal } from '../AutoTailoring/AutoTailoringModal'
import { PreventOriginalResumeEditModal } from '../AutoTailoring/PreventOriginalResumeEditModal'
import { CreateResumeWithLinkedin } from '../FillResumeModal/CreateResumeWithLinkedin/CreateResumeWithLinkedin'
import { DeleteSectionFunc, RenameSectionFunc } from '../Section'
import {
  RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_ID,
  ResumeDistributionAction,
} from '../ResumeDistributionV2'
import { DownloadButton, EditorMobileFooter } from '../PreviewPanel'
import { useStepByStepFeature } from './StepByStepFlow/hooks/useStepByStepFeature'
import { useTourConfig } from './DocumentTour'
import { BuilderType } from './ResumeSideMenu/types'
import { ElementHighlight } from './ElementHighlight'
import { ResumeSideMenu } from './ResumeSideMenu/ResumeSideMenu'
import ATSPersonalDetailsSection from './ATSResumeEditor/PersonalDetailsSection'
import {
  EditorPanel,
  EditorScroll,
  EditorContent,
  EditorSubtitle,
  LoaderContainer,
  Loader,
  UserNavigation,
  JobSearch,
  JobSearchContainer,
  VerticalDivider,
  EditorTitleWrapper,
  ResumeScoreWrapper,
  OptimizerBannerWrapper,
  ResumeHeaderBottomSpace,
  ResumeDistributionActionContainer,
  EditorPanelSwitcherWrapper,
  FloatingButton,
  MobileTopSpacer,
} from './styles'
import { EditorUnblockedSnackbar } from './EditorUnblockedSnackbar'
import { StepByStepFlow } from './StepByStepFlow/StepByStepFlow'
import { useScrollTo } from './hooks/useScrollTo'
import {
  FinnishTailoringButton,
  StartTailoringButton,
  getAutotailoringHeaderHeight,
  TailoringFromPublicJobBoardBanner,
} from './TailoringFromPublicJobBoard'

type Props = {
  resumeState: ResumeEditorStore
  editorState: GeneralEditorStore
  mobilePanelSwitcherItems: EditorPanelSwitcherItem<EditorPanelType>[]
  showResumeDistributionModal?: (format: FORMATS) => void
  handleDownloadDocument: DownloadDocumentHandler
  handleShareDocument?: () => void
}

const RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_SHOWN = 'RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_SHOWN'

export const ResumeEditor = (props: Props) => {
  const {
    resumeState,
    editorState,
    mobilePanelSwitcherItems,
    showResumeDistributionModal,
    handleDownloadDocument,
    handleShareDocument,
  } = props
  const dispatch = useDispatch()
  const config = useConfig()
  const { i18n } = useI18n()
  const { startInitTourModal } = useTourConfig()
  const useExample = useTypedSelector(state => state.resumeEditor.useExample)
  const prefillResumeSection = useTypedSelector(state => state.resumeEditor.prefillResumeSection)
  const isPremiumUser = useTypedSelector(state => state.user.data?.hasPremiumFeatures ?? false)
  const autoTailoringOpenedModal = useTypedSelector(selectors.autoTailoringOpenedModal)
  const aiResume = useTypedSelector(state => state.resumeEditor.aiResume)
  const showPrefillResumeModalProp = useTypedSelector(
    state => state.resumeEditor.resume?.showPrefillResumeModal,
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const resumeDistributionId = searchParams.get('resume-distribution-id')
  const downloadCurrentDocumentFormat = searchParams.get(DOWNLOAD_CURRENT_DOCUMENT_FORMAT_URL_PARAM)
  const optimizerSection = useTypedSelector(state => state.resumeEditor.optimizerSection)
  const isLinkedToJobPosting = !!optimizerSection.jobPostingAPIData.jobPostingId
  const isJobPostingDataFetched =
    optimizerSection.fetchJobPostingDataStatus === FetchStatuses.loaded
  const isOptimizerModalOpen = useTypedSelector(state => state.resumeEditor.isOptimizerModalOpen)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const isPageScrolled = useTypedSelector(selectors.isPageScrolled)
  const createResumeWithLinkedin = useTypedSelector(selectors.createResumeWithLinkedin)
  const isConversationalBuilderOpen = useTypedSelector(uiSelectors.isConversationalBuilderOpen)
  const openedCard = useTypedSelector(selectors.openedCard)
  const { isRbpStepFlowActive } = useStepByStepFeature()
  const {
    resume,
    improveResumePanel: { isOpen: isImproveResumePanelOpen },
    editorPanelType,
  } = resumeState
  const isResumeLoaded = !!resume
  const { resumeTemplates, levels, locales } = editorState
  const { showAIResumeModal, generateResumeStatus } = aiResume
  const { showUseExampleModal } = useExample
  const { successStatus } = prefillResumeSection
  const isResumeOptimizerFeatureOn = config?.features.resumeOptimizer
  const isPrefillResumeFeatureOn = config?.features.newStartScreen !== 'no_screen'
  const {
    aiResumeDraft,
    startWithExamples,
    uploadResume,
    superApp,
    international,
    dashboardV3,
    dashboardV2,
    roBannerTrigger,
    resumeBuilderAiCopilot,
  } = config?.features || {}

  const isDashboardV3 = superApp || international || (dashboardV3 && !dashboardV2)
  const hostname = window.location.hostname
  const isCareerIo = hostname === 'career.io' || hostname === 'staging.career.io'
  const isOptimizerBannerTriggerEnabled = !!roBannerTrigger && roBannerTrigger !== 'control'
  const isCreateResumeWithLinkedinModalOpen =
    createResumeWithLinkedin.showCreateResumeWithLinkedInModal
  const mediaQueries = useMediaQueries()
  const { isAboveTablet, isPhone, isTablet } = mediaQueries
  const { canScrollBottom, canScrollTop } = useScrollState({ topThreshold: 100 })

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )
  const autotailoringHeaderHeight = autoTailoringFromPublicJobBoard.enabled
    ? getAutotailoringHeaderHeight(mediaQueries)
    : 0

  const showResumeOptimizerBanner =
    isResumeOptimizerFeatureOn &&
    isJobPostingDataFetched &&
    !isLinkedToJobPosting &&
    !isImproveResumePanelOpen &&
    !isOptimizerBannerTriggerEnabled &&
    (!autoTailoringFromPublicJobBoard.enabled ||
      autoTailoringFromPublicJobBoard.step === AutoTailoringFromPublicJobBoardStep.finished)

  const showPrefillResumeModal =
    !isOptimizerModalOpen &&
    showPrefillResumeModalProp &&
    (aiResumeDraft || startWithExamples || uploadResume) &&
    isPrefillResumeFeatureOn

  const showScoreContainer =
    !autoTailoringFromPublicJobBoard.enabled ||
    autoTailoringFromPublicJobBoard.step === AutoTailoringFromPublicJobBoardStep.finished ||
    isRbpStepFlowActive

  const showAutoTailoringPrefillBanner =
    autoTailoringFromPublicJobBoard.showPrefillBanner &&
    !showResumeOptimizerBanner &&
    autoTailoringFromPublicJobBoard.step !== AutoTailoringFromPublicJobBoardStep.finished

  useScrollTo(resumeTemplates.length && resume ? resume : null)

  useEffectOnMount(() => {
    const urlParams = queryString.parse(window.location.search)
    const preserveAIResumeDraft = urlParams && urlParams.showAIResumeDraft
    if (!preserveAIResumeDraft) dispatch(actions.resetAIResumeData())
  })

  const handleSimpleFieldUpdate = useCallback(
    (name: any, value: any, debounce?: boolean) => {
      dispatch(actions.updateSimpleField({ name, value, debounce }))

      switch (name) {
        case 'name':
          trackInternalEvent('rename_resume')
          break
        case 'color':
          trackInternalEvent('change_resume_color', { template: resume?.template })
          break
        case 'locale':
          trackInternalEvent('change_resume_locale', { locale: value })
          break
        default:
          break
      }
    },
    [dispatch, resume?.template],
  )

  const handleResumeNameChange = useCallback(
    (value: string) => {
      dispatch(actions.updateSimpleField({ name: 'name', value, debounce: true }))
    },
    [dispatch],
  )

  const handleLocaleSelect = useCallback(
    (value: string) => {
      dispatch(actions.updateSimpleField({ name: 'locale', value, debounce: true }))
    },
    [dispatch],
  )

  const handlePrefillModalClose = useCallback(() => {
    dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.notStarted))
    dispatch(actions.setResumeUploadStatus(ResumeUploadStatus.notStarted))
    dispatch(
      actions.updateSimpleField({
        name: 'showPrefillResumeModal',
        value: false,
        debounce: false,
      }),
    )
  }, [dispatch])

  const updateCard: UpdateCardFunc = useCallback(
    (sectionId, cardId, values, debounce, isCustom) =>
      dispatch(actions.updateCard({ sectionId, cardId, values, debounce, isCustom })),
    [dispatch],
  )

  const addCard = useCallback(
    (cardId: string, sectionName: ResumeSectionId, options?: { shouldOpen: boolean }) =>
      dispatch(actions.addCard({ sectionName, cardId, options, debounce: true })),
    [dispatch],
  )

  const deleteCard: CardOperationFunc = useCallback(
    (cardId, sectionName, isCustom = false) =>
      dispatch(actions.deleteCard({ sectionName, cardId, isCustom })),
    [dispatch],
  )

  const addCustomSection = useCallback(
    (externalId: string, options: AddCustomSectionPayload['options']) =>
      dispatch(actions.addCustomSection({ externalId, options, debounce: true })),
    [dispatch],
  )

  const moveCard: MoveCardFunc = useCallback(
    payload => dispatch(actions.moveCard(payload)),
    [dispatch],
  )

  const moveSection = useCallback(
    (payload: MoveSectionPayload) => dispatch(actions.moveSection(payload)),
    [dispatch],
  )

  const deleteSection: DeleteSectionFunc = useCallback(
    (id, options) => dispatch(actions.deleteSection({ id, options: { ...options } })),
    [dispatch],
  )

  const renameSection: RenameSectionFunc = useCallback(
    payload => dispatch(actions.renameSection({ ...payload, debounce: true })),
    [dispatch],
  )

  const handleEditorPanelTypeChange = useCallback(
    (type: EditorPanelType) => {
      dispatch(actions.setEditorPanelType(type))
      trackInternalEvent('switch_resume_editor_tab', {
        tab: type,
      })
    },
    [dispatch],
  )

  const addCardSubItem: CardOperationFunc = useCallback(
    (cardId, sectionName) => dispatch(actions.addCardSubItem({ sectionName, cardId })),
    [dispatch],
  )

  const moveCardSubItem: MoveSubItemFunc = useCallback(
    payload => dispatch(actions.moveCardSubItem(payload)),
    [dispatch],
  )

  const handleToggleConversationalBuilder = useCallback(() => {
    if (!isConversationalBuilderOpen) {
      trackInternalEvent('chat_open', { label: 'builder' })
    }
    dispatch(uiActions.toggleConversationalBuilder(!isConversationalBuilderOpen))
  }, [dispatch, isConversationalBuilderOpen])

  useEffect(() => {
    if (generateResumeStatus === GenerateResumeStatus.finished || successStatus) {
      if (successStatus) {
        dispatch(actions.setResumeUploadSuccessStatus(false))
      }
      startInitTourModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateResumeStatus, successStatus])

  useEffect(() => {
    /** If the user has selected Tailor to Job Posting flow from dashboard
     * and if the prefill modal has been set to open, call the handler function
     */
    if (isOptimizerModalOpen && resume?.showPrefillResumeModal) {
      handlePrefillModalClose()
    }
  }, [isOptimizerModalOpen, resume?.showPrefillResumeModal, handlePrefillModalClose])

  useEffect(() => {
    dispatch(actions.setIsPageScrolled(canScrollTop))
  }, [canScrollTop, dispatch])

  useEffect(() => {
    const isHighlightShown = localStorage.getItem(RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_SHOWN)
    if (resumeDistributionId && !isHighlightShown) {
      dispatch(
        actions.setHighlightedElement({
          title: i18n.t(
            'builder.resume_distribution.version_3.resume_distribution_action.highlighted_element.title',
          ),
          content: i18n.t(
            'builder.resume_distribution.version_3.resume_distribution_action.highlighted_element.description',
          ),
          target: `[data-highlight-id=${RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_ID}]`,
          onClose: () => {
            localStorage.setItem(RESUME_DISTRIBUTION_ACTION_HIGHLIGHT_SHOWN, 'true')
          },
        }),
      )
    }
  }, [dispatch, i18n, resumeDistributionId])

  useEffect(() => {
    if (!resumeDistributionId || !resume?.id) {
      return
    }
    if (resume?.showPrefillResumeModal) {
      dispatch(
        actions.updateSimpleField({
          name: 'showPrefillResumeModal',
          value: false,
          debounce: true,
        }),
      )
    }
    if (resume?.data?.stepByStepFlow?.currentStep) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: null,
            },
          },
          debounce: true,
        }),
      )
    }
  }, [dispatch, resume, resumeDistributionId])

  useEffect(() => {
    if (downloadCurrentDocumentFormat && isResumeLoaded) {
      handleDownloadDocument({ format: downloadCurrentDocumentFormat as FORMATS })
      searchParams.delete(DOWNLOAD_CURRENT_DOCUMENT_FORMAT_URL_PARAM)
      setSearchParams(searchParams, { replace: true })
    }
  }, [
    downloadCurrentDocumentFormat,
    handleDownloadDocument,
    searchParams,
    setSearchParams,
    isResumeLoaded,
  ])

  if (!resume || resumeTemplates.length === 0) {
    return (
      <EditorPanel>
        <LoaderContainer>
          <Loader src={loaderImage} />
        </LoaderContainer>
      </EditorPanel>
    )
  }

  const isATSTemplate = ATS_TEMPLATES.includes(resume.template as TemplateName)

  return (
    <>
      {isDashboardV3 &&
        !(isTablet && autoTailoringFromPublicJobBoard.enabled) &&
        !isConversationalBuilderOpen && (
          <ResumeSideMenu isOnline={isOnline} builderType={BuilderType.resumeBuilder} />
        )}
      {!isRbpStepFlowActive && (
        <JobSearchContainer>
          <JobSearch
            id="job-search-solution"
            hideDivider
            showBubbles
            viewPoint="editor"
            resumeId={resume.id}
          />
          {!isPhone && !isConversationalBuilderOpen && <VerticalDivider />}
        </JobSearchContainer>
      )}

      {isConversationalBuilderOpen ? (
        <ConversationalBuilder />
      ) : (
        <EditorPanel data-testid="editor-panel">
          {!isDashboardV3 && isOnline && <UserNavigation hasFeatures />}
          {!isRbpStepFlowActive ? (
            <EditorScroll>
              <EditorContent>
                {!isAboveTablet && (
                  <>
                    {!autoTailoringFromPublicJobBoard.enabled && <MobileTopSpacer />}
                    <EditorPanelSwitcherWrapper $stickyTop={autotailoringHeaderHeight}>
                      <EditorPanelSwitcher
                        activePanelType={editorPanelType}
                        handlePanelTypeChange={handleEditorPanelTypeChange}
                        items={mobilePanelSwitcherItems}
                      />
                    </EditorPanelSwitcherWrapper>
                  </>
                )}
                <EditorTitleWrapper>
                  {resumeDistributionId && (
                    <ResumeDistributionActionContainer>
                      <ResumeDistributionAction
                        resume={resume}
                        newlyCreatedResumeId={resumeDistributionId}
                      />
                    </ResumeDistributionActionContainer>
                  )}
                  <EditorTitle
                    onChange={handleResumeNameChange}
                    value={resume.name ? resume.name : i18n.t('builder.resume_editor.untitled')}
                  />

                  <EditorSubtitle>
                    <LanguageSelect
                      selected={resume.locale}
                      onSelect={handleLocaleSelect}
                      options={locales}
                    />
                  </EditorSubtitle>
                </EditorTitleWrapper>

                {showScoreContainer && (
                  <ResumeScoreWrapper
                    $isPageScrolled={isPageScrolled}
                    $stickyTop={autotailoringHeaderHeight}
                  >
                    <ResumeScore />
                  </ResumeScoreWrapper>
                )}

                {showAutoTailoringPrefillBanner && (
                  <OptimizerBannerWrapper>
                    <TailoringFromPublicJobBoardBanner />
                  </OptimizerBannerWrapper>
                )}

                {showResumeOptimizerBanner && (
                  <OptimizerBannerWrapper>
                    <TailorResumeBanner transitionProps={{ marginTop: 0 }} />
                  </OptimizerBannerWrapper>
                )}

                <ResumeHeaderBottomSpace />

                <TunerEditorProvider resume={resume}>
                  <Anchor id={SectionNames.details} scrollOffsetTop={70}>
                    {isATSTemplate ? (
                      <ATSPersonalDetailsSection resume={resume} />
                    ) : (
                      <PersonalDetailsSection resume={resume} />
                    )}
                  </Anchor>

                  <SectionsList
                    resume={resume}
                    updateCard={updateCard}
                    addCard={addCard}
                    addCustomSection={addCustomSection}
                    deleteCard={deleteCard}
                    moveCard={moveCard}
                    moveSection={moveSection}
                    levels={levels}
                    updateSimpleField={handleSimpleFieldUpdate}
                    renameSection={renameSection}
                    deleteSection={deleteSection}
                    addCardSubItem={addCardSubItem}
                    moveCardSubItem={moveCardSubItem}
                  />
                </TunerEditorProvider>

                {!isAboveTablet && (
                  <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>
                    {autoTailoringFromPublicJobBoard.enabled ? (
                      <>
                        {autoTailoringFromPublicJobBoard.step ===
                          AutoTailoringFromPublicJobBoardStep.step1 && <StartTailoringButton />}

                        {autoTailoringFromPublicJobBoard.step ===
                          AutoTailoringFromPublicJobBoardStep.finished && (
                          <FinnishTailoringButton />
                        )}
                      </>
                    ) : (
                      <DownloadButton
                        showResumeDistributionModal={showResumeDistributionModal}
                        handleDownloadDocument={handleDownloadDocument}
                        handleShareDocument={handleShareDocument}
                        shouldShowIcon={false}
                      />
                    )}
                  </EditorMobileFooter>
                )}

                {editorPanelType !== 'customization' &&
                  !(isPhone && openedCard) &&
                  resumeBuilderAiCopilot && (
                    <FloatingButton onClick={handleToggleConversationalBuilder}>
                      <AiCoach />
                      {i18n.t('builder.conversational_builder.ask_ai_writer')}
                    </FloatingButton>
                  )}
              </EditorContent>
            </EditorScroll>
          ) : (
            <StepByStepFlow
              resume={resume}
              updateCard={updateCard}
              addCard={addCard}
              addCustomSection={addCustomSection}
              deleteCard={deleteCard}
              moveCard={moveCard}
              moveSection={moveSection}
              levels={levels}
              updateSimpleField={handleSimpleFieldUpdate}
              renameSection={renameSection}
              deleteSection={deleteSection}
              onAddCardSubItem={addCardSubItem}
              onSubItemMove={moveCardSubItem}
              scorePanelTopOffsetPx={autotailoringHeaderHeight}
            />
          )}

          {showPrefillResumeModal && <FillResumeModal onClose={handlePrefillModalClose} />}
          {showAIResumeModal && <CreateResumeWithAI />}
          {showUseExampleModal && <UseExample />}
          {isPremiumUser && isCareerIo && <JobSearchButton resumeEditor />}
          {autoTailoringOpenedModal === 'keywords_selection' && (
            <BeforeAutoTailoringKeywordsModal
              isConfirmClosingDisabled={autoTailoringFromPublicJobBoard.enabled}
            />
          )}
          {autoTailoringOpenedModal === 'auto_tailoring' && <AutoTailoringModal />}
          <PreventOriginalResumeEditModal />
          <ElementHighlight />
          <EditorUnblockedSnackbar />
          <FillResumeByUrl />
          {isCreateResumeWithLinkedinModalOpen && <CreateResumeWithLinkedin />}
        </EditorPanel>
      )}
    </>
  )
}
